import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { RequireAuth } from "@/auth/RequireAuth";
import { HubSidebarLayout } from "@/components/layout/HubSidebarLayout";
import { WebsiteLayout } from "@/components/layout/WebsiteLayout";
import { ToastProvider } from "@/components/common/Toast";
import { TenantProvider } from "@/context/TenantContext";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { PlaceholderPage } from "@/pages/PlaceholderPage";
import { ThemeDetailPage } from "@/pages/themes/ThemeDetailPage";
import { ThemesListPage } from "@/pages/themes/ThemesListPage";
import { EscalationsListPage } from "@/pages/escalations/EscalationsListPage";
import { CreditsPage } from "@/pages/tenants/credits/CreditsPage";
// ATE-CR-159
import { SystemConfigPage } from "@/pages/system-config";
// ATE-CR-159
import { VerificationsPage } from "@/pages/verifications/VerificationsPage";
import {
  ROUTE_ESCALATIONS,
  ROUTE_HOME,
  ROUTE_LOGIN,
  // ATE-CR-164
  ROUTE_NOTIFICATIONS_VERIFICATION_DETAIL,
  ROUTE_NOTIFICATIONS_VERIFICATIONS,
  ROUTE_OPERATORS,
  ROUTE_SERVICE_REQUESTS,
  ROUTE_SYSTEM_CONFIG,
  ROUTE_TENANTS,
  ROUTE_TENANT_BRAND,
  ROUTE_TENANT_CREDITS,
  ROUTE_TENANT_ROLES,
  ROUTE_TENANT_USERS,
  ROUTE_THEME_DETAIL,
  ROUTE_THEMES,
  ROUTE_THEMES_CATEGORIES,
} from "@/constants/routes";

/** The route tree without its own router — easier to mount inside
 * MemoryRouter for tests. The top-level <App /> wraps this in a BrowserRouter. */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Public / unauthenticated surface */}
      <Route element={<WebsiteLayout />}>
        <Route path={ROUTE_LOGIN} element={<LoginPage />} />
      </Route>

      {/* Authenticated admin surface */}
      <Route
        element={
          <RequireAuth>
            <TenantProvider>
              <HubSidebarLayout />
            </TenantProvider>
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route
          path={ROUTE_TENANTS}
          element={<PlaceholderPage title="Tenants" />}
        />
        {/* CR-111: Themes top-level. Categories placeholder under /themes. */}
        <Route path={ROUTE_THEMES} element={<ThemesListPage />} />
        <Route
          path={ROUTE_THEMES_CATEGORIES}
          element={<PlaceholderPage title="Theme categories" />}
        />
        <Route path={ROUTE_THEME_DETAIL} element={<ThemeDetailPage />} />

        {/* CR-111: one-release redirects from the old /catalog IA so bookmarks
            keep working. /catalog/items is intentionally NOT redirected —
            catalog_items moves to a future Store CR; until then, the * →
            / fallback at the bottom of this tree redirects unknown paths
            back to Home. */}
        <Route
          path="/catalog"
          element={<Navigate to={ROUTE_THEMES} replace />}
        />
        <Route
          path="/catalog/themes"
          element={<Navigate to={ROUTE_THEMES} replace />}
        />
        <Route
          path="/catalog/previews"
          element={<Navigate to={ROUTE_THEMES} replace />}
        />
        <Route
          path="/catalog/categories"
          element={<Navigate to={ROUTE_THEMES_CATEGORIES} replace />}
        />

        <Route
          path={ROUTE_SERVICE_REQUESTS}
          element={<PlaceholderPage title="Service Requests" />}
        />
        <Route
          path={ROUTE_OPERATORS}
          element={<PlaceholderPage title="Operators" />}
        />
        {/* ATE-CR-159: System Config landing page links to Verify Email + future ops tools */}
        <Route path={ROUTE_SYSTEM_CONFIG} element={<SystemConfigPage />} />

        {/* ATE-CR-117: escalations + per-tenant child routes */}
        <Route path={ROUTE_ESCALATIONS} element={<EscalationsListPage />} />
        {/* ATE-CR-159: HQ-only email-notification verification admin page */}
        <Route
          path={ROUTE_NOTIFICATIONS_VERIFICATIONS}
          element={<VerificationsPage />}
        />
        {/* ATE-CR-164: deep-link variant so refresh / shareable URL hydrates
            the status panel from `verificationId` in the URL. */}
        <Route
          path={ROUTE_NOTIFICATIONS_VERIFICATION_DETAIL}
          element={<VerificationsPage />}
        />
        <Route
          path={ROUTE_TENANT_USERS}
          element={<PlaceholderPage title="Users" />}
        />
        <Route
          path={ROUTE_TENANT_ROLES}
          element={<PlaceholderPage title="Roles" />}
        />
        <Route path={ROUTE_TENANT_CREDITS} element={<CreditsPage />} />
        <Route
          path={ROUTE_TENANT_BRAND}
          element={<PlaceholderPage title="Brand" />}
        />
      </Route>

      {/* Unknown routes land back at the home shell */}
      <Route path="*" element={<Navigate to={ROUTE_HOME} replace />} />
    </Routes>
  );
}

// ATE-CR-117
export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </BrowserRouter>
  );
}
